import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Toolbar as MuiToolbar } from '@mui/material';

const Item = styled(Paper)(({ theme, bg }) => ({
  backgroundColor: bg || theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(0),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const ButtonsBar = ({
  styles,
  children,
  sx = {},
  title,
  buttons,
  toggleButtons = [],
  isProgress,
}) => {
  const navigate = useNavigate();

  const Header = () => {
    if (!title) return null;
    return (
      <Box sx={{ p: 0, m: 0 }}>
        <Typography
          variant={
            styles?.header?.titleVariant ? styles.header.titleVariant : 'h6'
          }
          sx={styles?.header?.title ? styles?.header?.title : null}
        >
          {title}
        </Typography>
      </Box>
    );
  };

  const ToolbarItem = ({ el }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClickMenu = (event) => {
      console.log(event.currentTarget);

      setAnchorEl(event.currentTarget);
    };
    if (el?.type === 'divider') {
      return (
        <Divider
          orientation={el?.orientation || 'vertical'}
          flexItem={el?.flexItem || false}
        />
      );
    }
    if (el.type === 'component') {
      return (
        <Tooltip title={el?.label} placement='top-start'>
          <Item>{el.component}</Item>
        </Tooltip>
      );
    }
    if (el.type === 'icon') {
      return (
        // <Tooltip title={el?.label} placement='top-start'>
        <Item bg={el?.bg}>
          <IconButton
            onClick={() => {
              if (el.link) {
                navigate(el.link);
              } else if (typeof el?.onClick === 'function') {
                el.onClick();
              } else {
                return false;
              }
            }}
            aria-label={el?.arialabel || 'settings'}
            size={el?.size || 'small'}
            color={el?.color || 'inherit'}
          >
            {el.icon}
          </IconButton>
        </Item>
        // </Tooltip>
      );
    }
    if (el.type === 'text') {
      return (
        <Tooltip title={el?.label} placement='top-start'>
          <Button
            color={el?.color || 'primary'}
            variant={el?.variant || 'text'}
            onClick={() => {
              if (el.link) {
                navigate(el.link);
              } else if (typeof el?.onClick === 'function') {
                el.onClick();
              } else {
                return false;
              }
            }}
          >
            {el.label || el.title}
          </Button>
        </Tooltip>
      );
    }
    if (el.type === 'menu' && Array.isArray(el.items)) {
      return (
        <div>
          <Tooltip title={el?.label} placement='top-start'>
            <Item bg={el?.bg}>
              <IconButton
                id='menu-button'
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClickMenu}
                aria-label={el?.arialabel || 'settings'}
                size={el?.size || 'small'}
                color={el?.color || 'inherit'}
              >
                {el.icon}
              </IconButton>
            </Item>
          </Tooltip>
          <Menu
            id='basic-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={() => el.onClose()}
            MenuListProps={{
              'aria-labelledby': 'menu-button',
            }}
          >
            {el.items.map((item, index) => (
              <MenuItem
                key={item?.id || item?._id || index}
                onClick={() => el.onClose(item)}
              >
                {item.title || item.label || item.name}
              </MenuItem>
            ))}
          </Menu>
        </div>
      );
    }
    return null;
  };

  const GridMenu = ({ position }) => {
    const items = buttons.filter((item) => item?.position === position);
    if (items.length === 0) {
      return <Stack></Stack>;
    }

    let defaultXs = Math.floor(buttons.length / 12);
    if (defaultXs < 1) defaultXs = 1;
    if (defaultXs > 12) defaultXs = 12;

    return (
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        spacing={1}
        sx={{ p: 0, m: 0 }}
      >
        {items.map((el, index) => (
          <Grid item xs={el?.xs || true} key={index}>
            <ToolbarItem el={el} />
          </Grid>
        ))}
      </Stack>
    );
  };

  const ToggleMenu = ({ position }) => {
    const items = toggleButtons.filter((item) => item?.position === position);
    if (items.length === 0) {
      return null;
    }

    const ToggleButtonIcon = ({ button }) => {
      return (
        <ToggleButton
          value={button?.value}
          aria-label={button?.ariaLabel}
          onClick={button?.onClick}
        >
          {button?.icon}
        </ToggleButton>
      );
    };

    const ToggleButtonMenu = ({ button }) => {
      const [anchorEl, setAnchorEl] = React.useState(null);
      const open = Boolean(anchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };
      const handleClose = () => {
        setAnchorEl(null);
      };

      return (
        <>
          <ToggleButton
            onClick={handleClick}
            value={button?.value}
            aria-label={button?.ariaLabel}
          >
            {button?.icon}
          </ToggleButton>
          <Menu
            id='basic-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {button?.items?.map((item, index) => (
              <MenuItem
                key={item?.id || item?._id || index}
                onClick={() => {
                  button.onClose(item);
                  setAnchorEl(null);
                }}
              >
                {item.title || item.label || item.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      );
    };

    return (
      <ToggleButtonGroup
        size='small'
        value={'left'}
        exclusive
        onChange={() => {}}
        aria-label='text alignment'
        sx={{ p: 0, m: 0 }}
      >
        {items.map((btn, index) => {
          if (btn?.type === 'Divider') {
            return (
              <Divider
                key={index}
                orientation={btn?.orientation || 'vertical'}
                flexItem={btn?.flexItem || false}
              />
            );
          } else if (btn?.type === 'ToggleButton') {
            if (Array.isArray(btn?.items)) {
              return <ToggleButtonMenu button={btn} key={index} />;
            } else {
              return <ToggleButtonIcon button={btn} key={index} />;
            }
          } else {
            return null;
          }
        })}
      </ToggleButtonGroup>
    );
  };

  const Toolbar = ({ position }) => {
    if (Array.isArray(buttons)) {
      return <GridMenu position={position} />;
    }

    if (Array.isArray(toggleButtons)) {
      return <ToggleMenu position={position} />;
    }

    return null;
  };

  return (
    <>
      <Box
        sx={{
          p: 0,
          m: 0,
          minHeight: '100vh',
          maxWidth: '720px',
          ...sx,
        }}
      >
        <Paper
          // position='sticky'
          color='default' // или "inherit", "primary"
          // elevation={0}
          sx={
            {
              // top: { xs: 58, sm: 64 },
              // zIndex: effectiveZIndex,
              // backgroundColor: 'background.paper',
              // borderBottom: 1,
              // borderColor: 'divider',
              // boxShadow: 3,
            }
          }
        >
          <MuiToolbar
            variant='dense' // компактнее
            disableGutters
            sx={{
              minHeight: { xs: 48, sm: 56 },
              justifyContent: 'space-between',
              px: 2,
            }}
          >
            <Toolbar position='left' />
            <Header />
            <Toolbar position='right' />
          </MuiToolbar>
        </Paper>
        {isProgress && <LinearProgress />}
        {children}
      </Box>
    </>
  );
};

export default ButtonsBar;
